/**
 * iter322 — Multi-country marketplace location catalog.
 *
 * Structured Country → Region/Governorate → City → Area used by the
 * premium `<LocationSelector>` bottom sheet on:
 *   • Home "Near You" / Choose location
 *   • Shop filters (Phase B)
 *   • Post Ad "Where is this item/service located?" (Phase B)
 *
 * Keep this list ADDITIVE — once a country code ships, never delete
 * regions/cities (existing ads reference them by name). It's fine to
 * add new areas at any time.
 *
 * Coverage:
 *   Sudan (SD)          — primary market, most depth
 *   Egypt (EG)          — fast-growing diaspora
 *   South Sudan (SS), Chad (TD), Ethiopia (ET), Eritrea (ER),
 *   Uganda (UG), Kenya (KE)   — Horn-of-Africa neighbours
 *   UAE (AE), Qatar (QA), Saudi Arabia (SA) — GCC diaspora
 *
 * Each ad on the public surface displays general location only
 * (country + city + area). Exact GPS coordinates are NOT public per
 * the updated Privacy Policy.
 */

/**
 * @typedef {Object} CityNode
 * @property {string} name       English / canonical name (also stored on the ad).
 * @property {string=} ar        Arabic display name.
 * @property {string[]=} areas   Areas / markets / neighborhoods. Plain strings —
 *                                AR display is provided for the city level only
 *                                to keep the catalog compact.
 */

/**
 * @typedef {Object} RegionNode
 * @property {string} name
 * @property {string=} ar
 * @property {CityNode[]} cities
 */

/**
 * @typedef {Object} CountryNode
 * @property {string} countryCode  ISO 3166-1 alpha-2 (uppercase).
 * @property {string} countryName  English country name.
 * @property {string} countryNameAr Arabic country name.
 * @property {string} dialCode     E.164 country code (helpful for phone display).
 * @property {RegionNode[]} regions
 */

/** @type {CountryNode[]} */
export const COUNTRIES = [
  {
    countryCode: 'SD',
    countryName: 'Sudan',
    countryNameAr: 'السودان',
    dialCode: '+249',
    regions: [
      {
        name: 'Khartoum',
        ar: 'الخرطوم',
        cities: [
          { name: 'Khartoum',       ar: 'الخرطوم',  areas: [
            { name: 'Downtown',     ar: 'وسط الخرطوم' },
            { name: 'Riyadh',       ar: 'الرياض' },
            { name: 'Khartoum 2',   ar: 'الخرطوم 2' },
            { name: 'Burri',        ar: 'بري' },
            { name: 'Manshiya',     ar: 'المنشية' },
            { name: 'Souq Arabi',   ar: 'السوق العربي' },
            { name: 'Souq Mahalli', ar: 'السوق المحلي' },
          ] },
          { name: 'Omdurman',       ar: 'أم درمان', areas: [
            { name: 'Souq Libya',   ar: 'سوق ليبيا' },
            { name: 'Souq Omdurman',ar: 'سوق أم درمان' },
            { name: 'Al Thawra',    ar: 'الثورة' },
            { name: 'Al Mowrada',   ar: 'المورده' },
            { name: 'Wad Nubawi',   ar: 'ود نوباوي' },
            { name: 'Beit El Mal',  ar: 'بيت المال' },
          ] },
          { name: 'Khartoum North', ar: 'بحري',     areas: [
            { name: 'Bahri Centre', ar: 'وسط بحري' },
            { name: 'Kafouri',      ar: 'كافوري' },
            { name: 'Halfaia',      ar: 'الحلفايا' },
            { name: 'Shambat',      ar: 'شمبات' },
            { name: 'Al Mazad',     ar: 'المزاد' },
            { name: 'Industrial Area', ar: 'المنطقة الصناعية' },
          ] },
        ],
      },
      {
        name: 'Red Sea',
        ar: 'البحر الأحمر',
        cities: [
          { name: 'Port Sudan', ar: 'بورتسودان', areas: [
            { name: 'Daim Suakin', ar: 'ديم سواكن' },
            { name: 'Daim Arab',   ar: 'ديم العرب' },
            { name: 'Souq',        ar: 'السوق' },
            { name: 'Transit',     ar: 'الترانزيت' },
            { name: 'Salalab',     ar: 'سلالاب' },
          ] },
          { name: 'Suakin',     ar: 'سواكن',     areas: [
            { name: 'Old City',    ar: 'المدينة القديمة' },
            { name: 'Port',        ar: 'الميناء' },
          ] },
        ],
      },
      {
        name: 'Al Jazirah',
        ar: 'الجزيرة',
        cities: [
          { name: 'Wad Madani', ar: 'ود مدني', areas: [
            { name: 'Hantoub',     ar: 'حنتوب' },
            { name: 'Wad Hassouna',ar: 'ود حسونة' },
            { name: 'Souq',        ar: 'السوق' },
            { name: 'University',  ar: 'الجامعة' },
          ] },
          { name: 'Rufaa',      ar: 'رفاعة',  areas: [
            { name: 'Souq',        ar: 'السوق' },
            { name: 'Hai Al Jamea',ar: 'حي الجامعة' },
          ] },
        ],
      },
      {
        name: 'Kassala',
        ar: 'كسلا',
        cities: [
          { name: 'Kassala',    ar: 'كسلا',   areas: [
            { name: 'Khatmiya',    ar: 'الختمية' },
            { name: 'Souq',        ar: 'السوق' },
            { name: 'New Halfa',   ar: 'حلفا الجديدة' },
            { name: 'Toteel',      ar: 'توتيل' },
          ] },
        ],
      },
      {
        name: 'Gedaref',
        ar: 'القضارف',
        // iter335 + iter336 — Operator-supplied hierarchy for Gedaref
        // state. Gedaref City county has 16 town/neighbourhood entries
        // in Arabic (سوق/حي = bazaar/neighbourhood). East Galabat
        // county includes Doka. Spelling on two entries
        // ("حي المفارقة" / "حي المفرقعات" and "سوق اللمجة" / "سوق
        // اللملجه") is the operator's transcription — flagged for
        // future review but kept verbatim per instruction.
        cities: [
          { name: 'Gedaref City',    ar: 'مدينة القضارف', areas: [
            { name: 'Souq Al Kabeer',    ar: 'السوق الكبير' },
            { name: 'Souq Al Shaabi',    ar: 'السوق الشعبي' },
            { name: 'Souq Daim Al Noor', ar: 'سوق ديم النور' },
            { name: 'Daim Al Noor',      ar: 'حي ديم النور' },
            { name: 'Daim Hamad',        ar: 'حي ديم حمد' },
            { name: 'Al Jamhuria',       ar: 'حي الجمهورية' },
            { name: 'Al Matar',          ar: 'حي المطار' },
            { name: 'Al Soyool',         ar: 'حي السيول' },
            { name: 'Al Thawra',         ar: 'حي الثورة' },
            { name: 'Al Muwathafeen',    ar: 'حي الموظفين' },
            { name: 'Al Mafaraqaat',     ar: 'حي المفرقعات', spelling_review: true },
            { name: 'Al Sadaqa',         ar: 'حي الصداقة' },
            { name: 'Salama Al Bay',     ar: 'حي سلامة البي' },
            { name: 'Al Rabea',          ar: 'حي الرابعة' },
            { name: 'Al Ashlaq',         ar: 'حي الأشلاق' },
            { name: 'Souq Al Lamlaja',   ar: 'سوق اللملجه', spelling_review: true },
          ] },
          { name: 'East Galabat',    ar: 'القلابات الشرقية', areas: [
            { name: 'Doka',          ar: 'دوكة' },
            { name: 'Al Galabat',    ar: 'القلابات' },
          ] },
          { name: 'West Galabat',    ar: 'القلابات الغربية', areas: [] },
          { name: 'Al Faw',          ar: 'الفاو',           areas: [] },
          { name: 'Al Rahad',        ar: 'الرهد',           areas: [] },
          { name: 'Al Showak',       ar: 'الشواك',          areas: [] },
        ],
      },
      {
        name: 'Sennar',
        ar: 'سنار',
        cities: [
          { name: 'Sennar',     ar: 'سنار',    areas: [
            { name: 'Sennar Town', ar: 'مدينة سنار' },
            { name: 'Singa',       ar: 'سنجة' },
            { name: 'Dali',        ar: 'الدالي' },
          ] },
        ],
      },
      {
        name: 'White Nile',
        ar: 'النيل الأبيض',
        cities: [
          { name: 'Kosti',      ar: 'كوستي',   areas: [
            { name: 'Souq',         ar: 'السوق' },
            { name: 'Hai Al Bahri', ar: 'حي البحري' },
          ] },
          { name: 'Rabak',      ar: 'ربك',     areas: [
            { name: 'Souq',         ar: 'السوق' },
          ] },
        ],
      },
      {
        name: 'Blue Nile',
        ar: 'النيل الأزرق',
        cities: [
          { name: 'Damazin',    ar: 'الدمازين', areas: [
            { name: 'Souq',         ar: 'السوق' },
            { name: 'Roseires',     ar: 'الروصيرص' },
          ] },
        ],
      },
      {
        name: 'River Nile',
        ar: 'نهر النيل',
        cities: [
          { name: 'Atbara',     ar: 'عطبرة',    areas: [
            { name: 'Railway',      ar: 'السكة حديد' },
            { name: 'Souq',         ar: 'السوق' },
            { name: 'Mukabraab',    ar: 'المقابراب' },
          ] },
          { name: 'Shendi',     ar: 'شندي',     areas: [
            { name: 'Souq',         ar: 'السوق' },
            { name: 'Old Town',     ar: 'البلدة القديمة' },
          ] },
          { name: 'Berber',     ar: 'بربر',     areas: [
            { name: 'Souq',         ar: 'السوق' },
          ] },
        ],
      },
      {
        name: 'Northern',
        ar: 'الشمالية',
        cities: [
          { name: 'Dongola',    ar: 'دنقلا',    areas: [
            { name: 'Souq',         ar: 'السوق' },
            { name: 'Karima',       ar: 'كريمة' },
          ] },
          { name: 'Merowe',     ar: 'مروي',     areas: [
            { name: 'Old Town',     ar: 'البلدة القديمة' },
            { name: 'Power Station', ar: 'محطة الطاقة' },
          ] },
        ],
      },
      {
        name: 'North Kordofan',
        ar: 'شمال كردفان',
        cities: [
          { name: 'El Obeid',   ar: 'الأبيض',   areas: [
            { name: 'Souq',           ar: 'السوق' },
            { name: 'Hai Al Jamhuria',ar: 'حي الجمهورية' },
            { name: 'Sherkila',       ar: 'شركيلا' },
          ] },
        ],
      },
      {
        name: 'South Kordofan',
        ar: 'جنوب كردفان',
        cities: [
          { name: 'Kadugli',    ar: 'كادقلي',   areas: [
            { name: 'Souq',         ar: 'السوق' },
          ] },
        ],
      },
      {
        name: 'West Kordofan',
        ar: 'غرب كردفان',
        cities: [
          { name: 'El Fula',    ar: 'الفولة',   areas: [] },
          { name: 'Babanusa',   ar: 'بابنوسة',  areas: [] },
        ],
      },
      {
        name: 'North Darfur',
        ar: 'شمال دارفور',
        cities: [
          { name: 'El Fasher',  ar: 'الفاشر',  areas: [
            { name: 'Souq',         ar: 'السوق' },
            { name: 'Hai Al Jamea', ar: 'حي الجامعة' },
          ] },
        ],
      },
      {
        name: 'South Darfur',
        ar: 'جنوب دارفور',
        cities: [
          { name: 'Nyala',      ar: 'نيالا',   areas: [
            { name: 'Souq',         ar: 'السوق' },
            { name: 'Hai Al Salam', ar: 'حي السلام' },
          ] },
        ],
      },
      {
        name: 'West Darfur',
        ar: 'غرب دارفور',
        cities: [
          { name: 'El Geneina', ar: 'الجنينة', areas: [
            { name: 'Souq',         ar: 'السوق' },
          ] },
        ],
      },
      {
        name: 'Central Darfur',
        ar: 'وسط دارفور',
        cities: [
          { name: 'Zalingei',   ar: 'زالنجي',  areas: [] },
        ],
      },
      {
        name: 'East Darfur',
        ar: 'شرق دارفور',
        cities: [
          { name: 'Ed Daein',   ar: 'الضعين',  areas: [] },
        ],
      },
    ],
  },
  {
    countryCode: 'EG',
    countryName: 'Egypt',
    countryNameAr: 'مصر',
    dialCode: '+20',
    regions: [
      {
        name: 'Cairo',
        ar: 'القاهرة',
        cities: [
          { name: 'Nasr City',     ar: 'مدينة نصر',     areas: ['Abbas El Akkad', 'City Stars', 'Mostafa El Nahhas', 'Makram Ebeid', 'Tayaran'] },
          { name: 'Maadi',         ar: 'المعادي',       areas: ['Degla', 'Corniche', 'Old Maadi', 'New Maadi'] },
          { name: 'Heliopolis',    ar: 'مصر الجديدة',   areas: ['Korba', 'Triumph', 'Roxy', 'Sheraton', 'Almaza'] },
          { name: 'Downtown',      ar: 'وسط البلد',     areas: ['Talaat Harb', 'Tahrir', 'Ramses'] },
          { name: 'New Cairo',     ar: 'القاهرة الجديدة', areas: ['Fifth Settlement', 'Rehab', 'Madinaty'] },
          { name: 'Shoubra',       ar: 'شبرا',          areas: ['Shoubra El Kheima', 'Shoubra Misr'] },
        ],
      },
      {
        name: 'Giza',
        ar: 'الجيزة',
        cities: [
          { name: '6th of October', ar: '6 أكتوبر',     areas: ['Al Hosary', 'Sheikh Zayed', 'Dreamland'] },
          { name: 'Giza',           ar: 'الجيزة',       areas: ['Dokki', 'Mohandessin', 'Agouza', 'Pyramids'] },
          { name: 'Haram',          ar: 'الهرم',        areas: ['Faisal', 'Talbiya'] },
        ],
      },
      {
        name: 'Alexandria',
        ar: 'الإسكندرية',
        cities: [
          { name: 'Alexandria',     ar: 'الإسكندرية',   areas: ['Smouha', 'Miami', 'Sidi Gaber', 'Stanley', 'Sporting', 'Bolkly', 'Mandara'] },
          { name: 'Borg El Arab',   ar: 'برج العرب',    areas: ['Industrial Zone', 'New Borg'] },
        ],
      },
      {
        name: 'Qalyubia',
        ar: 'القليوبية',
        cities: [
          { name: 'Banha',          ar: 'بنها',         areas: ['Town Centre', 'Souq'] },
          { name: 'Qalyub',         ar: 'قليوب',        areas: ['Town Centre'] },
        ],
      },
      {
        name: 'Sharqia',
        ar: 'الشرقية',
        cities: [
          { name: 'Zagazig',        ar: 'الزقازيق',     areas: ['University', 'Souq'] },
          { name: '10th of Ramadan', ar: 'العاشر من رمضان', areas: ['Industrial Zone'] },
        ],
      },
      {
        name: 'Dakahlia',
        ar: 'الدقهلية',
        cities: [
          { name: 'Mansoura',       ar: 'المنصورة',     areas: ['University', 'Toreel', 'Souq'] },
        ],
      },
      {
        name: 'Aswan',
        ar: 'أسوان',
        cities: [
          { name: 'Aswan',          ar: 'أسوان',        areas: ['Town Centre', 'Souq'] },
        ],
      },
      {
        name: 'Luxor',
        ar: 'الأقصر',
        cities: [
          { name: 'Luxor',          ar: 'الأقصر',       areas: ['East Bank', 'West Bank', 'Souq'] },
        ],
      },
      {
        name: 'Red Sea',
        ar: 'البحر الأحمر',
        cities: [
          { name: 'Hurghada',       ar: 'الغردقة',      areas: ['Sakkala', 'El Dahar', 'El Mamsha'] },
        ],
      },
      {
        name: 'South Sinai',
        ar: 'جنوب سيناء',
        cities: [
          { name: 'Sharm El Sheikh', ar: 'شرم الشيخ',   areas: ['Naama Bay', 'Hadaba', 'Old Market'] },
        ],
      },
    ],
  },
  {
    countryCode: 'SS',
    countryName: 'South Sudan',
    countryNameAr: 'جنوب السودان',
    dialCode: '+211',
    regions: [
      { name: 'Central Equatoria', ar: 'الاستوائية الوسطى', cities: [{ name: 'Juba', ar: 'جوبا', areas: ['Konyo Konyo', 'Custom Market', 'Hai Cinema', 'Tongping'] }] },
      { name: 'Eastern Equatoria', ar: 'الاستوائية الشرقية', cities: [{ name: 'Torit', ar: 'توريت', areas: ['Town Centre'] }] },
      { name: 'Jonglei',           ar: 'جونقلي',           cities: [{ name: 'Bor',  ar: 'بور',  areas: ['Town Centre'] }] },
      { name: 'Unity',             ar: 'الوحدة',           cities: [{ name: 'Bentiu', ar: 'بانتيو', areas: ['Town Centre'] }] },
      { name: 'Upper Nile',        ar: 'أعالي النيل',      cities: [{ name: 'Malakal', ar: 'ملكال', areas: ['Town Centre'] }] },
      { name: 'Western Bahr el Ghazal', ar: 'غرب بحر الغزال', cities: [{ name: 'Wau', ar: 'واو', areas: ['Town Centre'] }] },
    ],
  },
  {
    countryCode: 'TD',
    countryName: 'Chad',
    countryNameAr: 'تشاد',
    dialCode: '+235',
    regions: [
      { name: "N'Djamena",     ar: 'إنجامينا',  cities: [{ name: "N'Djamena", ar: 'إنجامينا', areas: ['Centre Ville', 'Chagoua', 'Diguel', 'Klemat'] }] },
      { name: 'Logone Occidental', ar: 'لوغون الغربي', cities: [{ name: 'Moundou', ar: 'موندو', areas: ['Town Centre'] }] },
      { name: 'Ouaddai',       ar: 'وداي',     cities: [{ name: 'Abeche',     ar: 'أبشة',     areas: ['Town Centre', 'Marché'] }] },
      { name: 'Mayo-Kebbi Est', ar: 'مايو كبي الشرقية', cities: [{ name: 'Bongor', ar: 'بونغور', areas: ['Town Centre'] }] },
    ],
  },
  {
    countryCode: 'ET',
    countryName: 'Ethiopia',
    countryNameAr: 'إثيوبيا',
    dialCode: '+251',
    regions: [
      { name: 'Addis Ababa',  ar: 'أديس أبابا', cities: [{ name: 'Addis Ababa', ar: 'أديس أبابا', areas: ['Bole', 'Piazza', 'Merkato', 'Kazanchis', 'Old Airport'] }] },
      { name: 'Amhara',       ar: 'أمهرة',     cities: [
        { name: 'Bahir Dar',  ar: 'بحر دار',   areas: ['Town Centre'] },
        { name: 'Gondar',     ar: 'قندر',      areas: ['Town Centre'] },
      ] },
      { name: 'Oromia',       ar: 'أوروميا',    cities: [
        { name: 'Adama',      ar: 'أداما',     areas: ['Town Centre'] },
        { name: 'Jimma',      ar: 'جيما',      areas: ['Town Centre'] },
      ] },
      { name: 'Tigray',       ar: 'تيغراي',     cities: [{ name: 'Mekelle', ar: 'مقلي', areas: ['Town Centre'] }] },
      { name: 'Southern Nations', ar: 'الأمم الجنوبية', cities: [{ name: 'Hawassa', ar: 'هاوسا', areas: ['Town Centre'] }] },
      { name: 'Dire Dawa',    ar: 'دير داوا',  cities: [{ name: 'Dire Dawa', ar: 'دير داوا', areas: ['Kezira', 'Megala'] }] },
    ],
  },
  {
    countryCode: 'ER',
    countryName: 'Eritrea',
    countryNameAr: 'إريتريا',
    dialCode: '+291',
    regions: [
      { name: 'Maekel',       ar: 'مايكل',     cities: [{ name: 'Asmara',  ar: 'أسمرة',  areas: ['Centro', 'Tirvolo', 'Sembel'] }] },
      { name: 'Anseba',       ar: 'عنسبا',     cities: [{ name: 'Keren',   ar: 'كرن',    areas: ['Town Centre'] }] },
      { name: 'Northern Red Sea', ar: 'البحر الأحمر الشمالية', cities: [{ name: 'Massawa', ar: 'مصوع', areas: ['Old Port', 'Town Centre'] }] },
      { name: 'Southern',     ar: 'الجنوبية',  cities: [{ name: 'Mendefera', ar: 'مندفرا', areas: ['Town Centre'] }] },
    ],
  },
  {
    countryCode: 'UG',
    countryName: 'Uganda',
    countryNameAr: 'أوغندا',
    dialCode: '+256',
    regions: [
      { name: 'Central',  ar: 'الوسطى',  cities: [
        { name: 'Kampala', ar: 'كمبالا', areas: ['Central', 'Nakasero', 'Kololo', 'Bugolobi', 'Ntinda', 'Kisementi'] },
        { name: 'Entebbe', ar: 'عنتيبي', areas: ['Town Centre', 'Airport'] },
        { name: 'Wakiso',  ar: 'واكيسو', areas: ['Town Centre'] },
      ] },
      { name: 'Eastern',  ar: 'الشرقية',  cities: [
        { name: 'Jinja', ar: 'جينجا', areas: ['Town Centre', 'Bugembe'] },
        { name: 'Mbale', ar: 'مبالي', areas: ['Town Centre'] },
      ] },
      { name: 'Northern', ar: 'الشمالية', cities: [{ name: 'Gulu', ar: 'قولو', areas: ['Town Centre'] }] },
      { name: 'Western',  ar: 'الغربية',  cities: [{ name: 'Mbarara', ar: 'مبارارا', areas: ['Town Centre'] }] },
    ],
  },
  {
    countryCode: 'KE',
    countryName: 'Kenya',
    countryNameAr: 'كينيا',
    dialCode: '+254',
    regions: [
      { name: 'Nairobi',  ar: 'نيروبي', cities: [{ name: 'Nairobi', ar: 'نيروبي', areas: ['CBD', 'Westlands', 'Kilimani', 'Karen', 'Lavington', 'Eastleigh', 'South B', 'South C'] }] },
      { name: 'Coast',    ar: 'الساحل', cities: [
        { name: 'Mombasa', ar: 'مومباسا', areas: ['Nyali', 'Bamburi', 'Old Town', 'Kongowea'] },
        { name: 'Malindi', ar: 'مالندي',  areas: ['Town Centre'] },
      ] },
      { name: 'Central',  ar: 'الوسطى',  cities: [{ name: 'Nakuru', ar: 'ناكورو', areas: ['Town Centre'] }] },
      { name: 'Western',  ar: 'الغربية', cities: [{ name: 'Kisumu', ar: 'كيسومو', areas: ['Town Centre', 'Kondele'] }] },
      { name: 'Rift Valley', ar: 'الوادي المتصدع', cities: [{ name: 'Eldoret', ar: 'إلدوريت', areas: ['Town Centre'] }] },
    ],
  },
  {
    countryCode: 'AE',
    countryName: 'United Arab Emirates',
    countryNameAr: 'الإمارات العربية المتحدة',
    dialCode: '+971',
    regions: [
      { name: 'Dubai',          ar: 'دبي',          cities: [{ name: 'Dubai', ar: 'دبي', areas: ['Deira', 'Bur Dubai', 'Downtown', 'Marina', 'JLT', 'Business Bay', 'Al Barsha', 'Jumeirah', 'Al Quoz', 'Karama', 'International City'] }] },
      { name: 'Abu Dhabi',      ar: 'أبوظبي',       cities: [
        { name: 'Abu Dhabi',    ar: 'أبوظبي',       areas: ['Corniche', 'Khalidiya', 'Mussafah', 'Yas Island', 'Reem Island', 'Al Bateen'] },
        { name: 'Al Ain',       ar: 'العين',         areas: ['Town Centre', 'Al Jimi', 'Hili'] },
      ] },
      { name: 'Sharjah',        ar: 'الشارقة',       cities: [{ name: 'Sharjah', ar: 'الشارقة', areas: ['Al Qasimia', 'Al Majaz', 'Al Khan', 'Al Nahda'] }] },
      { name: 'Ajman',          ar: 'عجمان',         cities: [{ name: 'Ajman',    ar: 'عجمان',     areas: ['Al Nuaimiya', 'Corniche'] }] },
      { name: 'Ras Al Khaimah', ar: 'رأس الخيمة',    cities: [{ name: 'Ras Al Khaimah', ar: 'رأس الخيمة', areas: ['Al Nakheel', 'Old RAK'] }] },
      { name: 'Fujairah',       ar: 'الفجيرة',       cities: [{ name: 'Fujairah', ar: 'الفجيرة', areas: ['Town Centre'] }] },
      { name: 'Umm Al Quwain',  ar: 'أم القيوين',    cities: [{ name: 'Umm Al Quwain', ar: 'أم القيوين', areas: ['Town Centre'] }] },
    ],
  },
  {
    countryCode: 'QA',
    countryName: 'Qatar',
    countryNameAr: 'قطر',
    dialCode: '+974',
    regions: [
      { name: 'Doha',         ar: 'الدوحة',        cities: [{ name: 'Doha', ar: 'الدوحة', areas: ['West Bay', 'Pearl', 'Old Airport', 'Souq Waqif', 'Al Sadd', 'Al Rayyan'] }] },
      { name: 'Al Rayyan',    ar: 'الريان',        cities: [{ name: 'Al Rayyan', ar: 'الريان', areas: ['Old Town', 'New Rayyan'] }] },
      { name: 'Al Wakrah',    ar: 'الوكرة',        cities: [{ name: 'Al Wakrah', ar: 'الوكرة', areas: ['Town Centre', 'Souq'] }] },
      { name: 'Al Khor',      ar: 'الخور',         cities: [{ name: 'Al Khor', ar: 'الخور', areas: ['Town Centre'] }] },
    ],
  },
  {
    countryCode: 'SA',
    countryName: 'Saudi Arabia',
    countryNameAr: 'المملكة العربية السعودية',
    dialCode: '+966',
    regions: [
      { name: 'Riyadh',     ar: 'الرياض',     cities: [{ name: 'Riyadh', ar: 'الرياض', areas: ['Olaya', 'King Fahd District', 'Diplomatic Quarter', 'Al Malaz', 'Al Naseem', 'Al Murabba', 'Al Batha', 'Souq Al Zal'] }] },
      { name: 'Makkah',     ar: 'مكة المكرمة', cities: [
        { name: 'Jeddah',   ar: 'جدة',        areas: ['Al Balad', 'Al Hamra', 'Al Salama', 'Al Andalus', 'Al Rawdah', 'Corniche'] },
        { name: 'Makkah',   ar: 'مكة',        areas: ['Al Aziziyah', 'Al Misfalah', 'Al Awali'] },
        { name: 'Taif',     ar: 'الطائف',     areas: ['Town Centre'] },
      ] },
      { name: 'Madinah',    ar: 'المدينة المنورة', cities: [{ name: 'Madinah', ar: 'المدينة', areas: ['Central Haram Area', 'Quba', 'Al Aridh', 'Al Aqul'] }] },
      { name: 'Eastern',    ar: 'الشرقية',    cities: [
        { name: 'Dammam',   ar: 'الدمام',     areas: ['Corniche', 'Al Aziziyah', 'Al Faisaliyah'] },
        { name: 'Al Khobar', ar: 'الخبر',     areas: ['Corniche', 'Al Aqrabiyah', 'Olaya'] },
        { name: 'Dhahran',  ar: 'الظهران',    areas: ['Aramco Camp', 'Town Centre'] },
      ] },
      { name: 'Asir',       ar: 'عسير',       cities: [{ name: 'Abha', ar: 'أبها', areas: ['Town Centre'] }] },
      { name: 'Tabuk',      ar: 'تبوك',       cities: [{ name: 'Tabuk', ar: 'تبوك', areas: ['Town Centre'] }] },
      { name: 'Hail',       ar: 'حائل',       cities: [{ name: 'Hail',  ar: 'حائل',  areas: ['Town Centre'] }] },
    ],
  },
  // iter335 — Country-level coverage for Libya, Tanzania, Morocco
  // (operator's target list). City data is intentionally minimal so
  // we don't ship fake/incomplete catalogs — the picker exposes the
  // country + capital region only until the operator approves more.
  {
    countryCode: 'LY',
    countryName: 'Libya',
    countryNameAr: 'ليبيا',
    dialCode: '+218',
    regions: [
      { name: 'Tripoli',  ar: 'طرابلس', cities: [{ name: 'Tripoli',  ar: 'طرابلس',  areas: [] }] },
      { name: 'Benghazi', ar: 'بنغازي', cities: [{ name: 'Benghazi', ar: 'بنغازي',  areas: [] }] },
      { name: 'Misrata',  ar: 'مصراتة', cities: [{ name: 'Misrata',  ar: 'مصراتة',  areas: [] }] },
    ],
  },
  {
    countryCode: 'TZ',
    countryName: 'Tanzania',
    countryNameAr: 'تنزانيا',
    dialCode: '+255',
    regions: [
      { name: 'Dar es Salaam', ar: 'دار السلام', cities: [{ name: 'Dar es Salaam', ar: 'دار السلام', areas: [] }] },
      { name: 'Arusha',        ar: 'أروشا',       cities: [{ name: 'Arusha',        ar: 'أروشا',       areas: [] }] },
      { name: 'Mwanza',        ar: 'موانزا',      cities: [{ name: 'Mwanza',        ar: 'موانزا',      areas: [] }] },
    ],
  },
  {
    countryCode: 'MA',
    countryName: 'Morocco',
    countryNameAr: 'المغرب',
    dialCode: '+212',
    regions: [
      { name: 'Casablanca-Settat', ar: 'الدار البيضاء - سطات', cities: [{ name: 'Casablanca', ar: 'الدار البيضاء', areas: [] }] },
      { name: 'Rabat-Salé-Kénitra', ar: 'الرباط - سلا - القنيطرة', cities: [{ name: 'Rabat',     ar: 'الرباط',        areas: [] }] },
      { name: 'Marrakech-Safi',     ar: 'مراكش - آسفي',           cities: [{ name: 'Marrakech', ar: 'مراكش',          areas: [] }] },
      { name: 'Fès-Meknès',         ar: 'فاس - مكناس',            cities: [{ name: 'Fes',       ar: 'فاس',           areas: [] }] },
    ],
  },
  // iter336 — Operator-requested target-country expansion. Each entry
  // is intentionally CONSERVATIVE — country level + capital(s) only.
  // No fake counties/towns per spec: "It is better to show country +
  // major states/cities correctly than to add fake/incomplete lists."
  {
    countryCode: 'RW',
    countryName: 'Rwanda',
    countryNameAr: 'رواندا',
    dialCode: '+250',
    regions: [
      { name: 'Kigali',   ar: 'كيغالي',  cities: [{ name: 'Kigali',   ar: 'كيغالي', areas: [] }] },
      { name: 'Northern', ar: 'الشمالية', cities: [{ name: 'Musanze', ar: 'موسانزي', areas: [] }] },
      { name: 'Southern', ar: 'الجنوبية', cities: [{ name: 'Huye',    ar: 'هويى',    areas: [] }] },
    ],
  },
  {
    countryCode: 'CN',
    countryName: 'China',
    countryNameAr: 'الصين',
    dialCode: '+86',
    regions: [
      { name: 'Beijing',   ar: 'بكين',     cities: [{ name: 'Beijing',   ar: 'بكين',    areas: [] }] },
      { name: 'Shanghai',  ar: 'شنغهاي',   cities: [{ name: 'Shanghai',  ar: 'شنغهاي',  areas: [] }] },
      { name: 'Guangdong', ar: 'قوانغدونغ', cities: [{ name: 'Guangzhou', ar: 'قوانغجو', areas: [] }] },
    ],
  },
  {
    countryCode: 'US',
    countryName: 'United States',
    countryNameAr: 'الولايات المتحدة',
    dialCode: '+1',
    regions: [
      { name: 'California', ar: 'كاليفورنيا', cities: [{ name: 'Los Angeles', ar: 'لوس أنجلوس', areas: [] }] },
      { name: 'New York',   ar: 'نيويورك',    cities: [{ name: 'New York',    ar: 'نيويورك',    areas: [] }] },
      { name: 'Texas',      ar: 'تكساس',      cities: [{ name: 'Houston',     ar: 'هيوستن',     areas: [] }] },
    ],
  },
  {
    countryCode: 'CG',
    countryName: 'Republic of the Congo',
    countryNameAr: 'جمهورية الكونغو',
    dialCode: '+242',
    regions: [
      { name: 'Brazzaville', ar: 'برازافيل', cities: [{ name: 'Brazzaville', ar: 'برازافيل', areas: [] }] },
      { name: 'Pointe-Noire', ar: 'بوانت نوار', cities: [{ name: 'Pointe-Noire', ar: 'بوانت نوار', areas: [] }] },
    ],
  },
  {
    countryCode: 'CD',
    countryName: 'DR Congo',
    countryNameAr: 'جمهورية الكونغو الديمقراطية',
    dialCode: '+243',
    regions: [
      { name: 'Kinshasa',   ar: 'كينشاسا',   cities: [{ name: 'Kinshasa',   ar: 'كينشاسا',   areas: [] }] },
      { name: 'Lubumbashi', ar: 'لوبومباشي', cities: [{ name: 'Lubumbashi', ar: 'لوبومباشي', areas: [] }] },
    ],
  },
];

/** Default country shown when no selection has been made yet. */
export const DEFAULT_COUNTRY_CODE = 'SD';

/**
 * Helper — lookup by country code.
 * @param {string} code
 * @returns {CountryNode|undefined}
 */
export function getCountry(code) {
  if (!code) return undefined;
  return COUNTRIES.find(c => c.countryCode === code.toUpperCase());
}

/**
 * Helper — find a region under a country.
 * @param {string} code
 * @param {string} regionName
 * @returns {RegionNode|undefined}
 */
export function getRegion(code, regionName) {
  const country = getCountry(code);
  if (!country || !regionName) return undefined;
  return country.regions.find(r => r.name === regionName);
}

/**
 * Helper — find a city under (country, region).
 * @param {string} code
 * @param {string} regionName
 * @param {string} cityName
 * @returns {CityNode|undefined}
 */
export function getCity(code, regionName, cityName) {
  const region = getRegion(code, regionName);
  if (!region || !cityName) return undefined;
  return region.cities.find(c => c.name === cityName);
}

/**
 * Compose a human-readable label from a location selection.
 *
 * iter322 Phase A refinement — Resolution is now CASE-INSENSITIVE for
 * country (matches ISO code, English name, AND Arabic name) and
 * region / city (matches English `name` and Arabic `ar`). This means
 * ads stored with free-form lowercase `country: 'egypt'` still render
 * as canonical "Egypt" (EN) / "مصر" (AR), instead of leaking raw text.
 * @param {{country?:string, region?:string, city?:string, area?:string}} loc
 * @param {'en'|'ar'} [lang='en']
 * @returns {string} e.g. "Sudan · Khartoum · Omdurman · Souq Libya"
 */
export function formatLocationLabel(loc, lang) {
  const isAr = lang === 'ar';
  const country = loc?.country;
  const region = loc?.region;
  const city = loc?.city;
  const area = loc?.area;
  const sep = isAr ? ' · ' : ' · ';
  const parts = [];
  const norm = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

  let countryNode = null;
  if (country) {
    const lc = norm(country);
    const uc = lc.toUpperCase();
    countryNode = COUNTRIES.find(c =>
      c.countryCode === uc
      || norm(c.countryName) === lc
      || norm(c.countryNameAr) === lc
    );
    parts.push(countryNode ? (isAr ? countryNode.countryNameAr : countryNode.countryName) : country);
  }
  if (region) {
    const lc = norm(region);
    const regionNode = countryNode
      ? (countryNode.regions || []).find(r => norm(r.name) === lc || norm(r.ar) === lc)
      : null;
    parts.push(regionNode ? (isAr && regionNode.ar ? regionNode.ar : regionNode.name) : region);
  }
  if (city) {
    const lc = norm(city);
    let cityNode = null;
    let regionFallback = null; // ad.city may actually be a region name (e.g. 'Cairo' in EG schema)
    if (countryNode) {
      const regions = countryNode.regions || [];
      for (const r of regions) {
        cityNode = (r.cities || []).find(c => norm(c.name) === lc || norm(c.ar) === lc);
        if (cityNode) break;
        if (!regionFallback && (norm(r.name) === lc || norm(r.ar) === lc)) regionFallback = r;
      }
    }
    if (cityNode) parts.push(isAr && cityNode.ar ? cityNode.ar : cityNode.name);
    else if (regionFallback) parts.push(isAr && regionFallback.ar ? regionFallback.ar : regionFallback.name);
    else parts.push(city);
  }
  if (area) {
    // iter335 — Areas may now be `{name, ar}` objects (Sudan + future
    // countries) OR plain strings (legacy / unmigrated countries).
    // Resolve the city's `areas[]` so we can pick the AR label when
    // the locale is Arabic; fall back to the raw string for legacy
    // string-only areas.
    let areaLabel = typeof area === 'string' ? area : (area?.name || '');
    if (countryNode) {
      const lc = norm(typeof area === 'string' ? area : (area?.name || area?.ar || ''));
      outer: for (const r of (countryNode.regions || [])) {
        for (const c of (r.cities || [])) {
          for (const a of (c.areas || [])) {
            if (typeof a === 'string') {
              if (norm(a) === lc) {
                areaLabel = a;
                break outer;
              }
            } else if (a && typeof a === 'object') {
              if (norm(a.name) === lc || norm(a.ar) === lc) {
                areaLabel = isAr && a.ar ? a.ar : a.name;
                break outer;
              }
            }
          }
        }
      }
    }
    parts.push(areaLabel);
  }
  // iter337 — Append optional level-5 "Extra location details" (free
  // text). NEVER used for filtering — display only. Trimmed to keep
  // the breadcrumb tight.
  const extra = (typeof loc?.extra === 'string' ? loc.extra
                  : typeof loc?.location_extra === 'string' ? loc.location_extra
                  : '');
  const extraTrim = (extra || '').trim();
  if (extraTrim) parts.push(extraTrim);
  return parts.filter(Boolean).join(sep);
}

// iter335 — Public helper: resolve the localized label for an area
// string given the country/region/city context. Returns the raw
// string if no AR label is found (and a warning when the locale is AR
// — so missing translation surfaces in console / test logs).
export function getAreaLabel(countryCode, regionName, cityName, areaValue, locale = 'en') {
  if (!areaValue) return '';
  const isAr = locale === 'ar';
  const raw = typeof areaValue === 'string' ? areaValue : (areaValue?.name || areaValue?.ar || '');
  if (!countryCode) return raw;
  const norm = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');
  const country = COUNTRIES.find(c => c.countryCode === (countryCode || '').toUpperCase());
  if (!country) return raw;
  const region = (country.regions || []).find(r => norm(r.name) === norm(regionName) || norm(r.ar) === norm(regionName));
  if (!region) return raw;
  const city = (region.cities || []).find(c => norm(c.name) === norm(cityName) || norm(c.ar) === norm(cityName));
  if (!city) return raw;
  const lc = norm(raw);
  for (const a of (city.areas || [])) {
    if (typeof a === 'string' && norm(a) === lc) return a;
    if (a && typeof a === 'object' && (norm(a.name) === lc || norm(a.ar) === lc)) {
      if (isAr) {
        if (!a.ar && typeof console !== 'undefined') {
          // iter335 — Flag missing AR translations in dev so the team
          // can backfill the catalog; never crash the UI.
          console.warn(`[locations] missing AR translation for area '${a.name}' in ${countryCode} ${regionName} > ${cityName}`);
        }
        return a.ar || a.name;
      }
      return a.name;
    }
  }
  return raw;
}
